import {createAction, createAsyncThunk} from '@reduxjs/toolkit';

import {ORDERS_URL_PARAMS} from '../constants';
import type {
  CustomerNameResponse,
  FetchCustomersShipNamesArgs,
  FetchOrderDetailsArgs,
  FetchOrderDetailsCountsArgs,
  OrderDetailsCountsResponse,
  OrderDetailsResponse,
} from '../types';
import {API_ENDPOINTS} from '@/constants';

import {addAuthParams} from '@/features/auth/helpers';
import {request} from '@/shared/utils';

export const fetchOrderDetailsCounts = createAsyncThunk<
  OrderDetailsCountsResponse,
  FetchOrderDetailsCountsArgs
>('orders/details/counts/fetch', async ({po_number, order_date}) => {
  const urlParams = new URLSearchParams({
    [ORDERS_URL_PARAMS.PO_NUMBER]: po_number,
    [ORDERS_URL_PARAMS.ORDER_DATE]: order_date,
  });

  const modifiedUrlParams = addAuthParams(urlParams);

  return await request<OrderDetailsCountsResponse>({
    uri: `${API_ENDPOINTS.ORDERS_DETAILS_COUNTS}?${modifiedUrlParams}`,
    method: 'GET',
  });
});

export const fetchOrderDetails = createAsyncThunk<
  OrderDetailsResponse,
  FetchOrderDetailsArgs
>(
  'orders/details/fetch',
  async ({
    po_number,
    shipping_status,
    q,
    order_date,
    limit,
    ordering,
    offset,
    po_statuses,
  }) => {
    const urlParams = new URLSearchParams(
      Object.entries({
        [ORDERS_URL_PARAMS.PO_NUMBER]: po_number,
        [ORDERS_URL_PARAMS.SHIPPING_STATUS]: shipping_status,
        [ORDERS_URL_PARAMS.SEARCH]: q,
        [ORDERS_URL_PARAMS.ORDER_DATE]: order_date,
        [ORDERS_URL_PARAMS.LIMIT]: limit,
        [ORDERS_URL_PARAMS.ORDERING]: ordering,
        [ORDERS_URL_PARAMS.OFFSET]: offset,
        [ORDERS_URL_PARAMS.PO_STATUS]: po_statuses,
      })
        .filter(
          ([, v]) =>
            v != null && v !== '' && !(Array.isArray(v) && v.length === 0)
        )
        .map(([k, v]) => [k, String(v)])
    );

    const modifiedUrlParams = addAuthParams(urlParams);

    return await request<OrderDetailsResponse>({
      uri: `${API_ENDPOINTS.ORDERS_DETAILS}?${modifiedUrlParams}`,
      method: 'GET',
    });
  }
);

export const fetchCustomersBillNames = createAsyncThunk<CustomerNameResponse>(
  'customers/bill-names/fetch',
  async () => {
    const urlParams = new URLSearchParams();
    const modifiedUrlParams = addAuthParams(urlParams);

    return await request<CustomerNameResponse>({
      uri: `${API_ENDPOINTS.CUSTOMERS_BILL_NAMES}?${modifiedUrlParams}`,
      method: 'GET',
    });
  }
);

export const fetchCustomersShipNames = createAsyncThunk<
  CustomerNameResponse,
  FetchCustomersShipNamesArgs
>('customers/ship-names/fetch', async ({shipTo}) => {
  const urlParams = new URLSearchParams({
    [ORDERS_URL_PARAMS.CUSTOMER_NUMBER]: shipTo,
  });
  const modifiedUrlParams = addAuthParams(urlParams);

  return await request<CustomerNameResponse>({
    uri: `${API_ENDPOINTS.CUSTOMERS_SHIP_NAMES}?${modifiedUrlParams}`,
    method: 'GET',
  });
});

export const clearCustomersShipNames = createAction(
  'customers/ship-names/clear'
);

export const clearOrderDetails = createAction('order/details/clear');

interface ExportParams {
  po_number: string;
  order_date: string;
}

export const exportFile = ({po_number, order_date}: ExportParams) => {
  const urlParams = new URLSearchParams({
    [ORDERS_URL_PARAMS.PO_NUMBER]: po_number,
    [ORDERS_URL_PARAMS.ORDER_DATE]: order_date,
  });
  const modifiedUrlParams = addAuthParams(urlParams);

  return request({
    uri: `${API_ENDPOINTS.ORDERS_DETAILS_EXPORT}?${modifiedUrlParams}`,
    method: 'GET',
    contentType: null,
  });
};
